import React, { useEffect, useState } from 'react';
import {
  Button, IconButton, Panel, useDialogs,
} from '@michelin/acid-components';
import { Grid } from '@material-ui/core';
import { useTranslation } from '@michelin/central-provider';
import DataGrid, {
  Column, FilterRow, Scrolling, SearchPanel,
} from 'devextreme-react/data-grid';
import { getOptionLabel } from '@michelin/select-options-provider';
import { Delete as DeleteIcon, Edit, Visibility } from '@material-ui/icons';
import { cloneDeep, set } from 'lodash';
import uuid from 'uuid';
import FileModalPreview from 'components/FileUpload/FileModalPreview';
import { FileData } from 'components/FileUpload/FileUpload';
import { Contact, MechanicCertification } from '../../../Types';
import { PlainMessage } from '../../PlainMessage';
import { MechanicCertificationsDetail } from './Detail';
import usePermissionsSPP from '../../../hooks/usePermissionsSPP';

interface MechanicCertificationsListProps {
  contact: Contact,
  readOnly: boolean,
  onChange: (data: MechanicCertification[]) => void,
}

function MechanicCertificationsList(props: MechanicCertificationsListProps) {
  const { readOnly, contact } = props;

  const permissions = usePermissionsSPP();

  const [certificationsList, setCertificationsList] = useState<MechanicCertification[]>([]);
  const [certificationDetail, setCertificationDetail] = useState<MechanicCertification | null>(null);
  const [previewing, setPreviewing] = useState<boolean>(false);
  const [fileData, setFileData] = useState<FileData>({} as FileData);

  useEffect(() => () => {
    setCertificationsList([]);
    setCertificationDetail(null);
  }, []);

  useEffect(() => {
    setCertificationsList(contact.certifications ?? []);
  }, [contact]);

  const { t } = useTranslation();
  const { confirmDialog, errorDialog } = useDialogs();

  /// //////////////////////////////////////////////////////////////////
  /// ACTIONS METHODS
  /// //////////////////////////////////////////////////////////////////

  const hasDuplicates = (data: MechanicCertification[]): MechanicCertification[] => {
    const response: MechanicCertification[] = [];
    certificationsList.forEach((existingItem: MechanicCertification) => {
      data.forEach((newItem: MechanicCertification) => {
        if (existingItem.type === newItem.type && existingItem.job === newItem.job && existingItem.id !== newItem.id) {
          if (newItem.type === 'mechanical' && newItem.job === 'state_inspection') {
            if (existingItem.state === newItem.state) response.push(newItem);
          } else if (newItem.type === 'tire_certifications') {
            if (existingItem.certification === newItem.certification) response.push(newItem);
          } else {
            response.push(newItem);
          }
        }
      });
    });
    return response;
  };

  const onDetailsClose = () => {
    setCertificationDetail(null);
  };

  const onDetailsSave = (data: MechanicCertification[]) => {
    const duplicates: MechanicCertification[] = hasDuplicates(data);
    if (duplicates.length) {
      const certificationDuplicatesText = duplicates.map((cert) => {
        let str = '';
        if (permissions.location?.customer_country === 'ca') {
          str = getOptionLabel('spp_mechanical_certifications_services_ca', cert.type);
          if (cert.type === 'mechanical') str += ` ${getOptionLabel('spp_mechanical_certifications_jobs_mechanical_ca', cert.job)}`;
        } else {
          str = getOptionLabel('spp_mechanical_certifications_services_us', cert.type);
          if (cert.type === 'mechanical') {
            str += ` ${getOptionLabel('spp_mechanical_certifications_jobs_mechanical_us', cert.job)}`;
            if (cert.job === 'state_inspection') {
              str += ` - ${getOptionLabel('us_states', cert.state || '')}`;
            }
          }
        }
        if (cert.type === 'tire') str += ` ${getOptionLabel('spp_mechanical_certifications_jobs_tire', cert.job)}`;
        if (cert.type === 'tire_certifications') {
          str += ` ${getOptionLabel('spp_mechanical_certifications_jobs_tire_certifications', cert.job)}`;
          if (cert.job === 'ase_certified') {
            str += ` - ${getOptionLabel('spp_mechanical_certifications_certifications_ase', cert.certification || '')}`;
          } else if (cert.job === 'tia_certified') {
            str += ` - ${getOptionLabel('spp_mechanical_certifications_certifications_tia', cert.certification || '')}`;
          }
        }
        return str;
      }).join('\n\n ');
      errorDialog(
        t("You've attempted to add a duplicate certification with the following information:\n\n\n {{certificationDuplicates}}", {
          certificationDuplicates: certificationDuplicatesText,
        }).split('\n'),
        t('Duplicated certification found'),
      );
    } else {
      const list = cloneDeep(certificationsList).filter((cert: MechanicCertification) => data.some((newCert) => cert.id !== newCert.id));
      data.forEach((item) => {
        set(item, 'id', uuid.v4());
        list.push(item);
      });
      setCertificationsList(list);
      onDetailsClose();
      setCertificationDetail(null);
      props.onChange(list);
    }
  };

  const removeCertification = (data: MechanicCertification) => {
    const list = cloneDeep(certificationsList).filter((cert: MechanicCertification) => cert.id !== data.id);
    setCertificationsList(list);
    props.onChange(list);
  };

  /// //////////////////////////////////////////////////////////////////
  /// RENDER METHODS
  /// //////////////////////////////////////////////////////////////////

  const renderServiceTypeRow = (data: MechanicCertification) => {
    if (permissions.location?.customer_country === 'ca') return <>{getOptionLabel('spp_mechanical_certifications_services_ca', data.type)}</>;
    return <>{getOptionLabel('spp_mechanical_certifications_services_us', data.type)}</>;
  };

  const renderJobFunctionRow = (data: MechanicCertification) => {
    if (data.type === 'tire') return <>{getOptionLabel('spp_mechanical_certifications_jobs_tire', data.job)}</>;
    if (data.type === 'mechanical') {
      let label = (permissions.location?.customer_country === 'ca')
        ? getOptionLabel('spp_mechanical_certifications_jobs_mechanical_ca', data.job)
        : getOptionLabel('spp_mechanical_certifications_jobs_mechanical_us', data.job);
      if (data.job === 'state_inspection' && data.state) label += ` - ${getOptionLabel('us_states', data.state)}`;
      return <>{label}</>;
    }
    if (data.type === 'tire_certifications') {
      let label = getOptionLabel('spp_mechanical_certifications_jobs_tire_certifications', data.job);
      if (data.job === 'tia_certified') label += ` - ${getOptionLabel('spp_mechanical_certifications_certifications_tia', data.certification || '')}`;
      if (data.job === 'ase_certified') label += ` - ${getOptionLabel('spp_mechanical_certifications_certifications_ase', data.certification || '')}`;

      return <>{label}</>;
    }
    return '';
  };

  const renderDocumentsRow = (data: MechanicCertification) => {
    if (!data.file) return '';
    return data.file.name;
  };

  const renderActionsRow = (data: MechanicCertification) => (
    <div style={{ textAlign: 'right' }}>
      {data.file && (
        <IconButton
          aria-label={t('Preview')}
          style={{ margin: '8px' }}
          onClick={() => {
            if (!data.file) return;
            setFileData(data.file);
            setPreviewing(true);
          }}
          size="small"
        >
          <Visibility fontSize="small" />
        </IconButton>
      )}

      {!readOnly && (
        <>
          <IconButton
            aria-label={t('Edit')}
            style={{ margin: '8px' }}
            onClick={() => {
              setCertificationDetail(cloneDeep(data));
            }}
            size="small"
          >
            <Edit fontSize="small" />
          </IconButton>
          <IconButton
            aria-label={t('Delete')}
            style={{ margin: '8px' }}
            onClick={() => confirmDialog(
              t('Do you want to remove this certification?').split('\n'),
              t('Delete certification'),
              t('Remove certification'),
              t('Keep certification'),
              () => {
                removeCertification(data);
              },
            )}
            size="small"
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </>
      )}
    </div>
  );

  return (
    <>
      {(previewing) ? (
        <FileModalPreview
          onClose={() => { setPreviewing(false); }}
          title={t('File preview')}
          fileData={fileData || {} as FileData}
        />
      ) : null}
      <Panel
        title={t('Technician Mechanic Certifications & Training')}
        control={(
          <span style={{ position: 'relative', top: '4px' }}>
            {readOnly ? null : (
              <Button
                size="small"
                color="primary"
                onClick={() => setCertificationDetail({} as MechanicCertification)}
              >
                {t('Add')}
              </Button>
            )}
          </span>
        )}
      >
        <Grid container spacing={2}>
          <Grid item xs={12}>
            {certificationDetail ? (
              <MechanicCertificationsDetail
                action={certificationDetail && certificationDetail.id ? 'edit' : 'add'}
                defaultValues={certificationDetail ?? {} as MechanicCertification}
                onClose={onDetailsClose}
                onSave={onDetailsSave}
              />
            ) : null}
            {(!certificationsList.length
              ? (
                <PlainMessage
                  title={t('Technician Mechanic Certifications & Training List Empty')}
                  messages={t('There are currently no certifications & trainings for this contact.').split('\n')}
                />
              )
              : (
                <DataGrid
                  dataSource={certificationsList}
                  allowColumnResizing
                >
                  <FilterRow visible={false} />
                  <Scrolling mode="virtual" />
                  <SearchPanel visible={false} highlightCaseSensitive={false} />
                  <Column
                    caption={t('Service Type')}
                    cellRender={({ row }) => renderServiceTypeRow(row.data)}
                    width={200}
                  />
                  <Column
                    caption={t('Job Functions/Certifications')}
                    cellRender={({ row }) => renderJobFunctionRow(row.data)}
                  />
                  <Column
                    caption={t('Certification Documents')}
                    cellRender={({ row }) => renderDocumentsRow(row.data)}
                  />
                  <Column
                    cellRender={({ row }) => renderActionsRow(row.data)}
                    width={(readOnly ? 50 : 150)}
                  />
                </DataGrid>
              ))}
          </Grid>
        </Grid>
      </Panel>
    </>

  );
}

export {
  MechanicCertificationsList,
};
